/**
 * Pallet Calculator — staff tool
 *
 * Staff pick products (via the SKU search worker route), set quantities,
 * confirm/enter carton dimensions, and get Shippit pallet freight quotes.
 * All packing + quoting logic lives in the Cloudflare Worker; this file is
 * only the UI. Routes used: /sku-search, /pallet-item, /pallet-quote and
 * the suburb_search action.
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-pallet-calculator]');
  if (!root) return;

  const WORKER_URL = (root.dataset.workerUrl || '').replace(/\/$/, '');
  if (!WORKER_URL) {
    console.error('[PalletCalc] Missing worker URL');
    return;
  }

  const STACK_RULES = [
    { value: 'stackable', label: 'Stackable' },
    { value: 'no_stack_on_top', label: 'Nothing on top' },
    { value: 'own_pallet', label: 'Own pallet' },
  ];

  const state = {
    items: [], // { key, sku, title, image, qty, length_mm, width_mm, height_mm, weight_kg, stack_rule, dims_source, weight_source }
    destination: { postcode: '', suburb: '', state: '' },
    localities: [],
    calculating: false,
  };

  // ---------- elements ----------
  const els = {
    searchInput: root.querySelector('[data-pc-search]'),
    searchResults: root.querySelector('[data-pc-search-results]'),
    itemsBody: root.querySelector('[data-pc-items]'),
    itemsEmpty: root.querySelector('[data-pc-items-empty]'),
    itemsTable: root.querySelector('[data-pc-items-table]'),
    postcode: root.querySelector('[data-pc-postcode]'),
    suburb: root.querySelector('[data-pc-suburb]'),
    calculate: root.querySelector('[data-pc-calculate]'),
    status: root.querySelector('[data-pc-status]'),
    results: root.querySelector('[data-pc-results]'),
  };

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function setStatus(message, kind) {
    els.status.textContent = message || '';
    els.status.className = 'pallet-calc__status' + (kind ? ' pallet-calc__status--' + kind : '');
  }

  // ---------- product search ----------
  let searchTimer = null;
  let searchAbort = null;

  els.searchInput.addEventListener('input', function () {
    const q = els.searchInput.value.trim();
    clearTimeout(searchTimer);
    if (q.length < 2) {
      els.searchResults.hidden = true;
      els.searchResults.innerHTML = '';
      return;
    }
    searchTimer = setTimeout(function () { runSearch(q); }, 250);
  });

  document.addEventListener('click', function (e) {
    if (!els.searchResults.contains(e.target) && e.target !== els.searchInput) {
      els.searchResults.hidden = true;
    }
  });

  async function runSearch(q) {
    if (searchAbort) searchAbort.abort();
    searchAbort = new AbortController();
    try {
      const resp = await fetch(WORKER_URL + '/sku-search?q=' + encodeURIComponent(q), { signal: searchAbort.signal });
      const data = await resp.json();
      renderSearchResults(data.results || []);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('[PalletCalc] Search failed', err);
    }
  }

  function renderSearchResults(results) {
    if (results.length === 0) {
      els.searchResults.innerHTML = '<div class="pallet-calc__search-empty">No products found</div>';
      els.searchResults.hidden = false;
      return;
    }
    els.searchResults.innerHTML = results.map(function (r) {
      return '<button type="button" class="pallet-calc__search-item" data-sku="' + escapeHtml(r.sku) + '">' +
        (r.image ? '<img src="' + escapeHtml(r.image) + '" alt="" loading="lazy">' : '<span class="pallet-calc__search-noimg"></span>') +
        '<span class="pallet-calc__search-text"><strong>' + escapeHtml(r.title) +
        (r.variant_title ? ' — ' + escapeHtml(r.variant_title) : '') + '</strong>' +
        '<small>' + escapeHtml(r.sku) + (r.price ? ' · $' + escapeHtml(r.price) : '') + '</small></span>' +
        '</button>';
    }).join('');
    els.searchResults.hidden = false;

    els.searchResults.querySelectorAll('.pallet-calc__search-item').forEach(function (btn) {
      btn.addEventListener('click', function () {
        els.searchResults.hidden = true;
        els.searchInput.value = '';
        addItemBySku(btn.dataset.sku);
      });
    });
  }

  // ---------- items ----------
  let itemKey = 0;

  async function addItemBySku(sku) {
    const existing = state.items.find(function (i) { return i.sku === sku; });
    if (existing) {
      existing.qty += 1;
      renderItems();
      return;
    }

    setStatus('Loading ' + sku + '…');
    try {
      const resp = await fetch(WORKER_URL + '/pallet-item?sku=' + encodeURIComponent(sku));
      const data = await resp.json();
      if (!data.success) {
        setStatus('Could not load ' + sku + ' (' + (data.error || 'unknown error') + ')', 'error');
        return;
      }
      state.items.push({
        key: ++itemKey,
        sku: data.sku,
        title: data.title,
        image: data.image,
        qty: 1,
        length_mm: data.length_mm,
        width_mm: data.width_mm,
        height_mm: data.height_mm,
        weight_kg: data.weight_kg,
        stack_rule: data.stack_rule || 'stackable',
        dims_source: data.dims_source,
        weight_source: data.weight_source,
        sydney_metro_only: !!data.sydney_metro_only,
        pickup_only: !!data.pickup_only,
      });
      setStatus('');
      renderItems();
    } catch (err) {
      console.error('[PalletCalc] Item lookup failed', err);
      setStatus('Could not load ' + sku + ' — network error', 'error');
    }
  }

  // Dimensions are optional — items without them are packed by volume
  // derived from their Allied cubic weight (kg ÷ 250 = m³) in the worker
  function dimInput(item, field, placeholder) {
    const value = item[field];
    return '<input type="number" min="1" step="1" inputmode="numeric" ' +
      'class="pallet-calc__dim" ' +
      'data-key="' + item.key + '" data-field="' + field + '" ' +
      'value="' + (value != null && value !== '' ? escapeHtml(value) : '') + '" placeholder="' + placeholder + '">';
  }

  function renderItems() {
    const hasItems = state.items.length > 0;
    els.itemsEmpty.hidden = hasItems;
    els.itemsTable.hidden = !hasItems;

    els.itemsBody.innerHTML = state.items.map(function (item) {
      const weightMissing = !(item.weight_kg > 0);
      return '<tr data-row="' + item.key + '">' +
        '<td class="pallet-calc__cell-product">' +
          (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="">' : '') +
          '<span><strong>' + escapeHtml(item.title) + '</strong><small>' + escapeHtml(item.sku) +
          (weightMissing ? ' · <em class="pallet-calc__missing-note">enter cubic weight</em>' : '') +
          (item.pickup_only ? ' · <em class="pallet-calc__badge pallet-calc__badge--pickup">Pickup only</em>' : '') +
          (item.sydney_metro_only && !item.pickup_only ? ' · <em class="pallet-calc__badge pallet-calc__badge--metro">Sydney Metro only</em>' : '') +
          '</small></span></td>' +
        '<td><input type="number" min="1" step="1" class="pallet-calc__qty" data-key="' + item.key + '" data-field="qty" value="' + item.qty + '"></td>' +
        '<td class="pallet-calc__cell-dims">' +
          dimInput(item, 'length_mm', 'L') + ' × ' + dimInput(item, 'width_mm', 'W') + ' × ' + dimInput(item, 'height_mm', 'H') +
        '</td>' +
        '<td><input type="number" min="0.1" step="0.1" inputmode="decimal" ' +
          'class="pallet-calc__dim pallet-calc__weight' + (!(item.weight_kg > 0) ? ' pallet-calc__dim--missing' : '') + '" ' +
          'data-key="' + item.key + '" data-field="weight_kg" value="' + (item.weight_kg != null ? escapeHtml(item.weight_kg) : '') + '" placeholder="kg"></td>' +
        '<td><select class="pallet-calc__stack" data-key="' + item.key + '" data-field="stack_rule">' +
          STACK_RULES.map(function (r) {
            return '<option value="' + r.value + '"' + (item.stack_rule === r.value ? ' selected' : '') + '>' + r.label + '</option>';
          }).join('') +
        '</select></td>' +
        '<td><button type="button" class="pallet-calc__remove" data-remove="' + item.key + '" aria-label="Remove ' + escapeHtml(item.sku) + '">&times;</button></td>' +
        '</tr>';
    }).join('');

    els.itemsBody.querySelectorAll('input, select').forEach(function (input) {
      input.addEventListener('change', onItemFieldChange);
    });
    els.itemsBody.querySelectorAll('[data-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.items = state.items.filter(function (i) { return i.key !== parseInt(btn.dataset.remove, 10); });
        renderItems();
      });
    });
  }

  function onItemFieldChange(e) {
    const key = parseInt(e.target.dataset.key, 10);
    const field = e.target.dataset.field;
    const item = state.items.find(function (i) { return i.key === key; });
    if (!item) return;

    if (field === 'stack_rule') {
      item.stack_rule = e.target.value;
    } else if (field === 'qty') {
      item.qty = Math.max(1, parseInt(e.target.value, 10) || 1);
    } else {
      const num = parseFloat(e.target.value);
      item[field] = num > 0 ? num : null;
    }
    renderItems();
  }

  // ---------- destination ----------
  // AU states are fixed postcode ranges; used as a fallback so the tool still
  // quotes (Shippit only needs postcode + state) when the suburb-lookup API
  // (postcodeapi.com.au) is down.
  function stateFromPostcode(pc) {
    const n = parseInt(pc, 10);
    if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) return 'ACT';
    if ((n >= 1000 && n <= 2599) || (n >= 2619 && n <= 2899) || (n >= 2921 && n <= 2999)) return 'NSW';
    if ((n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)) return 'VIC';
    if ((n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)) return 'QLD';
    if (n >= 5000 && n <= 5999) return 'SA';
    if (n >= 6000 && n <= 6999) return 'WA';
    if (n >= 7000 && n <= 7999) return 'TAS';
    if (n >= 800 && n <= 999) return 'NT';
    return null;
  }

  function fallbackLocalities(postcode) {
    const st = stateFromPostcode(postcode);
    return st ? [{ location: 'UNKNOWN', state: st, fallback: true }] : [];
  }

  let suburbTimer = null;

  els.postcode.addEventListener('input', function () {
    const pc = els.postcode.value.trim();
    clearTimeout(suburbTimer);
    state.destination.postcode = pc;
    if (!/^\d{4}$/.test(pc)) {
      state.localities = [];
      renderSuburbs();
      return;
    }
    suburbTimer = setTimeout(function () { lookupSuburbs(pc); }, 300);
  });

  async function lookupSuburbs(postcode) {
    els.suburb.innerHTML = '<option>Looking up…</option>';
    els.suburb.disabled = true;
    try {
      // Don't hang on a dead upstream postcode API — fall back fast
      const abort = new AbortController();
      setTimeout(function () { abort.abort(); }, 4000);
      const resp = await fetch(WORKER_URL + '/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'suburb_search', query: postcode }),
        signal: abort.signal,
      });
      const data = await resp.json();
      state.localities = (data.localities && data.localities.locality) || [];
      if (state.localities.length === 0) state.localities = fallbackLocalities(postcode);
      renderSuburbs();
    } catch (err) {
      console.error('[PalletCalc] Suburb lookup failed', err);
      state.localities = fallbackLocalities(postcode);
      renderSuburbs();
    }
  }

  function renderSuburbs() {
    if (state.localities.length === 0) {
      els.suburb.innerHTML = '<option value="">— enter a valid postcode —</option>';
      els.suburb.disabled = true;
      state.destination.suburb = '';
      state.destination.state = '';
      return;
    }
    els.suburb.innerHTML = state.localities.map(function (loc, i) {
      const label = loc.fallback
        ? 'Suburb lookup unavailable — quoting as ' + loc.state
        : loc.location + ', ' + loc.state;
      return '<option value="' + i + '">' + escapeHtml(label) + '</option>';
    }).join('');
    els.suburb.disabled = false;
    selectSuburb(0);
  }

  function selectSuburb(index) {
    const loc = state.localities[index];
    if (!loc) return;
    state.destination.suburb = loc.location;
    state.destination.state = loc.state;
  }

  els.suburb.addEventListener('change', function () {
    selectSuburb(parseInt(els.suburb.value, 10) || 0);
  });

  // ---------- calculate ----------
  els.calculate.addEventListener('click', calculate);

  async function calculate() {
    if (state.calculating) return;

    if (state.items.length === 0) {
      setStatus('Add at least one product first.', 'error');
      return;
    }
    const missing = state.items.filter(function (i) { return !(i.weight_kg > 0); });
    if (missing.length > 0) {
      setStatus('Enter the cubic weight for: ' + missing.map(function (i) { return i.sku; }).join(', '), 'error');
      return;
    }
    if (!/^\d{4}$/.test(state.destination.postcode)) {
      setStatus('Enter a valid 4-digit destination postcode.', 'error');
      return;
    }
    if (!state.destination.state) {
      // Suburb lookup still pending or failed — never block on it
      const st = stateFromPostcode(state.destination.postcode);
      if (!st) {
        setStatus('Postcode not recognised — check it and try again.', 'error');
        return;
      }
      state.destination.state = st;
      state.destination.suburb = 'UNKNOWN';
      state.localities = fallbackLocalities(state.destination.postcode);
      renderSuburbs();
    }

    state.calculating = true;
    els.calculate.disabled = true;
    setStatus('Packing pallets and fetching Shippit quotes…');
    els.results.innerHTML = '';

    try {
      const resp = await fetch(WORKER_URL + '/pallet-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destination: state.destination,
          items: state.items.map(function (i) {
            return {
              sku: i.sku, title: i.title, qty: i.qty,
              length_mm: i.length_mm, width_mm: i.width_mm, height_mm: i.height_mm,
              weight_kg: i.weight_kg, stack_rule: i.stack_rule,
              sydney_metro_only: i.sydney_metro_only, pickup_only: i.pickup_only,
            };
          }),
        }),
      });
      const data = await resp.json();
      if (!resp.ok || (!data.pallets && !data.success)) {
        setStatus('Quote failed: ' + (data.error || resp.status), 'error');
        return;
      }
      setStatus(data.error ? 'Pallets packed, but Shippit quote failed: ' + data.error : '', data.error ? 'error' : '');
      renderResults(data);
    } catch (err) {
      console.error('[PalletCalc] Quote failed', err);
      setStatus('Quote failed — network error.', 'error');
    } finally {
      state.calculating = false;
      els.calculate.disabled = false;
    }
  }

  // ---------- results ----------
  function extractQuotes(quoteResponse) {
    // Same response shape the shipping estimator parses:
    // { response: [ { courier_type, service_level, success, price?, quotes?: [...] } ] }
    const raw = (quoteResponse && quoteResponse.response) || [];
    const flat = [];
    raw.forEach(function (q) {
      if (!q || q.success === false) return;
      if (q.courier_type === 'ClickAndCollect') return;
      if (Array.isArray(q.quotes) && q.quotes.length > 0) {
        q.quotes.forEach(function (inner) {
          flat.push({
            courier: q.courier_type || inner.courier_type || '',
            service: q.service_level || inner.service_level || '',
            price: parseFloat(inner.price),
            eta: inner.estimated_transit_time || inner.delivery_date || '',
          });
        });
      } else if (q.price !== undefined) {
        flat.push({
          courier: q.courier_type || '',
          service: q.service_level || '',
          price: parseFloat(q.price),
          eta: q.estimated_transit_time || '',
        });
      }
    });
    return flat
      .filter(function (q) { return !isNaN(q.price); })
      .sort(function (a, b) { return a.price - b.price; });
  }

  // Cubic-weight-estimated pallets have no real dimensions — showing fake
  // L×W×H misleads (a 1600 bath is longer than the pallet). Show what's
  // true: the load volume and the weight Allied is actually charged on.
  function formatDims(p) {
    if (p.method === 'volume' && p.load_m3 != null) {
      return '≈ ' + p.load_m3.toFixed(2) + ' m³ on a standard pallet';
    }
    return (p.length_mm / 1000).toFixed(2) + ' × ' + (p.width_mm / 1000).toFixed(2) +
      ' × ' + (p.height_mm / 1000).toFixed(2) + ' m';
  }

  function formatWeight(p) {
    let s = p.weight_kg.toFixed(1) + ' kg incl. pallet';
    if (p.goods_weight_kg != null) s += ' · charged at ' + p.goods_weight_kg.toFixed(1) + ' kg';
    return s;
  }

  function quoteTable(quotes, emptyMessage) {
    if (quotes.length === 0) {
      return '<p class="pallet-calc__no-quotes">' + emptyMessage + '</p>';
    }
    return '<table class="pallet-calc__quote-table"><thead><tr>' +
      '<th>Carrier</th><th>Service</th><th>Est. transit</th><th>Price (ex. margin)</th>' +
      '</tr></thead><tbody>' +
      quotes.map(function (q, i) {
        return '<tr' + (i === 0 ? ' class="pallet-calc__quote-best"' : '') + '>' +
          '<td>' + escapeHtml(q.courier) + '</td>' +
          '<td>' + escapeHtml(q.service) + '</td>' +
          '<td>' + (q.eta ? escapeHtml(q.eta) + (typeof q.eta === 'number' ? ' days' : '') : '—') + '</td>' +
          '<td><strong>$' + q.price.toFixed(2) + '</strong></td></tr>';
      }).join('') +
      '</tbody></table>';
  }

  function renderResults(data) {
    const pallets = data.pallets || [];
    const parcelItems = data.parcel_items || [];
    // Pallet freight goes with Allied Express only; small items ship
    // standard eParcel only (no express, no Allied)
    const palletQuotes = extractQuotes(data.quote).filter(function (q) {
      return /allied/i.test(q.courier);
    });
    const parcelQuotes = extractQuotes(data.parcel_quote).filter(function (q) {
      return q.courier === 'Eparcel';
    });
    const dest = escapeHtml(state.destination.suburb) + ' ' + escapeHtml(state.destination.postcode);

    let html = '';
    if (data.strategy) {
      html += '<p class="pallet-calc__strategy">' + escapeHtml(data.strategy) + '</p>';
    }

    const restricted = data.restricted || [];
    if (restricted.length > 0) {
      html += '<div class="pallet-calc__restricted"><h3>Not quoted — shipping restrictions</h3><ul>' +
        restricted.map(function (r) {
          return '<li>' + r.qty + ' × ' + escapeHtml(r.title) + ' <small>(' + escapeHtml(r.sku) + ')</small> — ' +
            escapeHtml(r.reason) + '</li>';
        }).join('') + '</ul></div>';
    }

    if (pallets.length > 0) {
      html += '<div class="pallet-calc__pallets"><h3>Pallet freight — ' + pallets.length +
        ' pallet' + (pallets.length === 1 ? '' : 's') + '</h3><div class="pallet-calc__pallet-grid">';
      pallets.forEach(function (p, i) {
        html += '<div class="pallet-calc__pallet' + (p.oversized ? ' pallet-calc__pallet--oversized' : '') + '">' +
          '<h4>Pallet ' + (i + 1) + (p.oversized ? ' (oversized item)' : '') + '</h4>' +
          '<p class="pallet-calc__pallet-meta">' + formatDims(p) + ' · ' + formatWeight(p) +
          (p.utilisation != null ? ' · ' + p.utilisation + '% full' : '') + '</p>' +
          (p.note ? '<p class="pallet-calc__pallet-note">' + escapeHtml(p.note) + '</p>' : '') +
          '<ul>' + p.contents.map(function (c) {
            return '<li>' + c.qty + ' × ' + escapeHtml(c.title) + ' <small>(' + escapeHtml(c.sku) + ')</small></li>';
          }).join('') + '</ul></div>';
      });
      html += '</div>' +
        '<h3 class="pallet-calc__quote-heading">Pallet freight (Allied Express) — ' + dest + '</h3>' +
        quoteTable(palletQuotes, 'Allied Express returned no quote for this weight/destination — ' +
          'check the Allied rate card on the Shippit account.') +
        '</div>';
    }

    if (parcelItems.length > 0) {
      html += '<div class="pallet-calc__parcels"><h3>Ship as parcels (AusPost/courier)</h3>' +
        '<ul class="pallet-calc__parcel-list">' + parcelItems.map(function (i) {
          return '<li>' + i.qty + ' × ' + escapeHtml(i.title) + ' <small>(' + escapeHtml(i.sku) +
            ' · ' + i.weight_kg + ' kg)</small></li>';
        }).join('') + '</ul>' +
        '<h3 class="pallet-calc__quote-heading">Parcel quotes (eParcel) — ' + dest + '</h3>' +
        quoteTable(parcelQuotes, 'eParcel returned no quote for this destination.') +
        '</div>';
    }

    // Combined total: cheapest of each leg
    const legs = [];
    if (pallets.length > 0) legs.push({ label: 'pallet', quote: palletQuotes[0] });
    if (parcelItems.length > 0) legs.push({ label: 'parcel', quote: parcelQuotes[0] });
    if (legs.length > 0 && legs.every(function (l) { return l.quote; })) {
      const total = legs.reduce(function (s, l) { return s + l.quote.price; }, 0);
      html += '<div class="pallet-calc__total"><strong>Best combination: $' + total.toFixed(2) + '</strong> — ' +
        legs.map(function (l) {
          return escapeHtml(l.quote.courier) + ' ' + l.label + ' $' + l.quote.price.toFixed(2);
        }).join(' + ') + '</div>';
    }

    html += '<button type="button" class="pallet-calc__copy" data-pc-copy>Copy summary</button>';
    els.results.innerHTML = html;

    const copyBtn = els.results.querySelector('[data-pc-copy]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        copySummary(pallets, palletQuotes, parcelItems, parcelQuotes);
        copyBtn.textContent = 'Copied ✓';
        setTimeout(function () { copyBtn.textContent = 'Copy summary'; }, 2000);
      });
    }
  }

  function copySummary(pallets, palletQuotes, parcelItems, parcelQuotes) {
    const lines = [];
    lines.push('Freight estimate — ' + state.destination.suburb + ' ' + state.destination.state + ' ' + state.destination.postcode);
    pallets.forEach(function (p, i) {
      lines.push('Pallet ' + (i + 1) + ': ' + formatDims(p) + ', ' + formatWeight(p));
      p.contents.forEach(function (c) {
        lines.push('  - ' + c.qty + ' x ' + c.title + ' (' + c.sku + ')');
      });
    });
    if (palletQuotes.length > 0) {
      lines.push('Pallet quotes:');
      palletQuotes.forEach(function (q) {
        lines.push('  ' + q.courier + ' ' + q.service + ': $' + q.price.toFixed(2));
      });
    }
    if (parcelItems.length > 0) {
      lines.push('Parcels:');
      parcelItems.forEach(function (i) {
        lines.push('  - ' + i.qty + ' x ' + i.title + ' (' + i.sku + ')');
      });
      if (parcelQuotes.length > 0) {
        lines.push('Parcel quotes:');
        parcelQuotes.forEach(function (q) {
          lines.push('  ' + q.courier + ' ' + q.service + ': $' + q.price.toFixed(2));
        });
      }
    }
    if (palletQuotes[0] && parcelItems.length > 0 && parcelQuotes[0]) {
      lines.push('Best combination: $' + (palletQuotes[0].price + parcelQuotes[0].price).toFixed(2));
    }
    navigator.clipboard.writeText(lines.join('\n')).catch(function (err) {
      console.error('[PalletCalc] Copy failed', err);
    });
  }

  renderItems();
  renderSuburbs();
})();
